import { useCallback, useEffect, useState } from "react";
import { Task } from "model/activitiesModel";
import { getUserSchema } from "common/utilities";
import { offerableKeys } from "components/custom-fields/authorizedKeys";
import type { LegacySchemaRow } from "components/custom-fields/legacyAdapter";
import type { FieldConfig, FieldType, FieldValue } from "model/CustomFields";

export enum SupportedUserDataTypes {
  string,
  number,
  date,
  boolean
}

export interface UserData {
  value: FieldValue;
  inCard: boolean;
  type: SupportedUserDataTypes | FieldType;
  fieldId?: string;
  config?: FieldConfig;
}

export type UserDataUpdate = Omit<UserData, "value"> & { value?: FieldValue };

const useUserDataKeys = (tasks: {
  todo: Task[];
  doing: Task[];
}): [string[]] => {
  //l'hook torna un'array di stringhe
  const [keys, setKeys] = useState<string[]>([]);

  /**
   * Le chiavi di un task, lette dallo **schema** e non dai dati.
   *
   * `userdata` porta anche i campi che l'operatore non può vedere: sono dati
   * legittimi, semplicemente non suoi. Derivarne l'elenco farebbe comparire un
   * campo nascosto fra le opzioni di filtro appena un task qualsiasi lo porta —
   * e ne rivelerebbe almeno l'esistenza. Basta un task su mille.
   *
   * Un task senza schema non offre niente: il silenzio è la risposta giusta
   * quando non si sa cosa si può mostrare.
   */
  const getKeys = useCallback((task: Task) => {
    if (task.customFieldDefinitions) {
      return task.customFieldDefinitions.map((field) => field.legacyKey ?? field.label);
    }
    return offerableKeys([getUserSchema(task.userschema) as LegacySchemaRow[]]);
  }, []);

  useEffect(() => {
    if (tasks) {
      let keys: string[] = [];
      const filterKeys = (keys: string[]) => (key: string) => !keys.includes(key); 
      for (const task of tasks.doing) {
        const newKeys = getKeys(task).filter(filterKeys(keys))
        keys = [...keys, ...newKeys];
      }
      for (const task of tasks.todo) {
        keys = [...keys, ...getKeys(task).filter(filterKeys(keys))];
      }
      setKeys(keys);
    }
  }, [tasks, getKeys]);

  return [keys];
};

export default useUserDataKeys;
